import { Api, ApiRequest, ResType } from '@api'
import { Logger } from '@utils'
import {
	GetSeasonQueryType,
	SeasonCreationType,
	SeasonDeletionType,
	SeasonModificationType,
	SeasonUpdatePathType,
	SeasonOpenPathType,
	SeasonQueueUpdateType,
} from '@schemas'

export const getSeason = async ({
	body = {},
	query = {
		year: '',
	} as GetSeasonQueryType,
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const uri = '/season'
		const req = new ApiRequest({
			method: 'get',
			uri: uri,
			body: body,
			query: query,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)

		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

export const createSeason = async ({
	body = {
		title: '',
		startDate: '',
		endDate: '',
		activeYn: 0,
		targetUsd: 0,
		targetQuantity: 0,
	} as SeasonCreationType,
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const uri = '/season'
		const req = new ApiRequest({
			method: 'post',
			uri: uri,
			body: body,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)

		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

export const deleteSeason = async ({
	params = { seasonId: '' } as SeasonDeletionType,
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const uri = `/season/${params.seasonId}`
		const req = new ApiRequest({
			method: 'delete',
			uri: uri,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)

		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

export const updateSeason = async ({
	params = { seasonId: '' } as SeasonUpdatePathType,
	body = {
		title: '',
		startDate: '',
		endDate: '',
		activeYn: 0,
		targetUsd: 0,
		targetQuantity: 0,
	} as SeasonModificationType,
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const uri = `/season/${params.seasonId}`
		const req = new ApiRequest({
			method: 'put',
			uri: uri,
			body: body,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)

		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

/**
 * 현재 판매중인 시즌 1건.
 *
 * 시즌 목록은 연도로 필터되지만 판매중 시즌은 연도와 무관하므로 따로 조회한다.
 * 판매 중지 상태면 season 이 null 로 온다.
 */
export const getOpenSeason = async ({ customHeaders = {} } = {}): Promise<
	ResType | undefined
> => {
	try {
		const uri = '/season/open'
		const req = new ApiRequest({
			method: 'get',
			uri: uri,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)

		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

/**
 * 판매 시즌 전환. 기존 판매중 시즌을 닫고 이 시즌을 연다.
 *
 * 판매 기간 밖이면 400 이 돌아온다(강행 경로 없음).
 * 호출부에서 result.status 를 확인할 것 — 에러도 ResType 으로 반환된다.
 */
export const openSeason = async ({
	params = { seasonId: '' } as SeasonOpenPathType,
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const uri = `/season/${params.seasonId}/open`
		const req = new ApiRequest({
			method: 'patch',
			uri: uri,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)

		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

/** 판매 중지. 판매중인 시즌이 없어지면 shop 은 전 상품 Sold Out 이 된다. */
export const closeSeason = async ({ customHeaders = {} } = {}): Promise<
	ResType | undefined
> => {
	try {
		const uri = '/season/close'
		const req = new ApiRequest({
			method: 'patch',
			uri: uri,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)

		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

/** 대기열 순서 일괄 갱신. 배열 순서가 순번이 되고, 빠진 시즌은 대기열에서 제거된다. */
export const updateSeasonQueue = async ({
	body = { seasonIdList: [] } as SeasonQueueUpdateType,
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const uri = '/season/queue'
		const req = new ApiRequest({
			method: 'put',
			uri: uri,
			body: body,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)

		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

const seasonApi = {
	getSeason: getSeason,
	createSeason: createSeason,
	deleteSeason: deleteSeason,
	updateSeason: updateSeason,
	getOpenSeason: getOpenSeason,
	openSeason: openSeason,
	closeSeason: closeSeason,
	updateSeasonQueue: updateSeasonQueue,
}

export default seasonApi
